import { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Loader2, Upload, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from './Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: 'mon', es: 'Lun', en: 'Mon' },
  { key: 'tue', es: 'Mar', en: 'Tue' },
  { key: 'wed', es: 'Mié', en: 'Wed' },
  { key: 'thu', es: 'Jue', en: 'Thu' },
  { key: 'fri', es: 'Vie', en: 'Fri' },
  { key: 'sat', es: 'Sáb', en: 'Sat' },
  { key: 'sun', es: 'Dom', en: 'Sun' },
];

const getImageSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const RouteForm = ({ route, onSuccess, onCancel }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isEditing = !!route;
  const imageInputRef = useRef(null);

  // Parse existing eta: strip " (+1)" suffix if present
  const existingEta = route?.eta || '';
  const existingEtaNextDay = existingEta.includes('(+1)');
  const existingEtaClean = existingEta.replace(' (+1)', '');

  // Parse existing route field: "DIRECTO" -> type=direct, "VIA XXX" -> type=via, viaStation=XXX
  const existingRouteField = route?.route || '';
  const existingRouteType = existingRouteField.startsWith('VIA ') ? 'via' : 'direct';
  const existingViaStation = existingRouteField.startsWith('VIA ') ? existingRouteField.replace('VIA ', '') : '';

  const [formData, setFormData] = useState({
    origin: route?.origin || '',
    destination: route?.destination || '',
    originAirport: route?.originAirport || '',
    destinationAirport: route?.destinationAirport || '',
    etd: route?.etd || '',
    etaTime: existingEtaClean,
    etaNextDay: existingEtaNextDay,
    routeType: existingRouteType,
    viaStation: existingViaStation,
    image: route?.image || '',
    days: route?.days || { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleDay = (day) => setFormData(prev => ({ ...prev, days: { ...prev.days, [day]: !prev.days[day] } }));

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'es' ? 'El archivo excede el límite de 2MB' : 'File exceeds 2MB limit');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'es' ? 'Tipo de archivo no válido' : 'Invalid file type');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleChange('image', response.data.url);
      toast.success(language === 'es' ? 'Imagen subida exitosamente' : 'Image uploaded successfully');
    } catch {
      toast.error(language === 'es' ? 'Ha ocurrido un error' : 'An error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      toast.error(language === 'es' ? 'La imagen es obligatoria' : 'Image is required');
      return;
    }
    setSaving(true);
    try {
      const eta = formData.etaNextDay ? `${formData.etaTime} (+1)` : formData.etaTime;
      const route_field = formData.routeType === 'via'
        ? `VIA ${formData.viaStation.toUpperCase()}`
        : 'DIRECTO';
      const payload = {
        ...formData,
        eta,
        route: route_field,
        createdBy: user?.name || user?.userId || 'unknown'
      };
      // clean up UI-only fields
      delete payload.etaTime;
      delete payload.etaNextDay;
      delete payload.routeType;
      delete payload.viaStation;
      if (isEditing) {
        await axios.put(`${API}/routes/${route.id}`, payload);
        toast.success(language === 'es' ? 'Ruta actualizada' : 'Route updated');
      } else {
        await axios.post(`${API}/routes`, payload);
        toast.success(language === 'es' ? 'Ruta creada' : 'Route created');
      }
      onSuccess();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(language === 'es' ? 'Ha ocurrido un error' : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar extraNav={
        <Button variant="ghost" onClick={onCancel}
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 gap-2 mt-2"
          data-testid="back-button">
          <ArrowLeft className="h-4 w-4" />
          {language === 'es' ? 'Volver' : 'Back'}
        </Button>
      } />

      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-8">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-barlow text-2xl lg:text-3xl font-bold uppercase tracking-tight text-[#1E3A5F]">
                {isEditing ? (language === 'es' ? 'Editar Ruta' : 'Edit Route') : (language === 'es' ? 'Agregar Ruta' : 'Add Route')}
              </h1>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                <Button type="submit" disabled={saving} className="bg-[#8B1538] hover:bg-[#6D102B] text-white uppercase tracking-wide">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'es' ? 'Guardar' : 'Save')}
                </Button>
              </div>
            </div>

            <Card className="border-slate-200 mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F]">
                  {language === 'es' ? 'Origen y Destino' : 'Origin & Destination'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Código Origen' : 'Origin Code'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.origin} onChange={e => handleChange('origin', e.target.value.toUpperCase())} placeholder="BOG" required maxLength={3} className="uppercase" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Código Destino' : 'Destination Code'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.destination} onChange={e => handleChange('destination', e.target.value.toUpperCase())} placeholder="LIM" required maxLength={3} className="uppercase" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Aeropuerto Origen' : 'Origin Airport'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.originAirport} onChange={e => handleChange('originAirport', e.target.value)} placeholder="Bogotá (BOG) El Dorado International Airport" required />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Aeropuerto Destino' : 'Destination Airport'} <span className="text-red-500">*</span></Label>
                  <Input value={formData.destinationAirport} onChange={e => handleChange('destinationAirport', e.target.value)} placeholder="Lima (LIM) Jorge Chávez International Airport" required />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F]">
                  {language === 'es' ? 'Horario' : 'Schedule'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">ETD <span className="text-red-500">*</span></Label>
                    <Input value={formData.etd} onChange={e => handleChange('etd', e.target.value)} placeholder="13:00" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1E3A5F] font-medium">ETA <span className="text-red-500">*</span></Label>
                    <Input value={formData.etaTime} onChange={e => handleChange('etaTime', e.target.value)} placeholder="15:35" required />
                    <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                      <Checkbox checked={formData.etaNextDay} onCheckedChange={v => handleChange('etaNextDay', v)} />
                      <span className="text-xs text-slate-600">{language === 'es' ? 'Día siguiente (+1)' : 'Next day (+1)'}</span>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Tipo de ruta' : 'Route type'} <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <Checkbox checked={formData.routeType === 'direct'} onCheckedChange={() => handleChange('routeType', 'direct')} />
                      <span className="text-sm font-medium text-slate-700">{language === 'es' ? 'Directo' : 'Direct'}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <Checkbox checked={formData.routeType === 'via'} onCheckedChange={() => handleChange('routeType', 'via')} />
                      <span className="text-sm font-medium text-slate-700">Vía</span>
                    </label>
                  </div>
                  {formData.routeType === 'via' && (
                    <Input
                      value={formData.viaStation}
                      onChange={e => handleChange('viaStation', e.target.value.toUpperCase())}
                      placeholder={language === 'es' ? 'Estación (ej: LIM)' : 'Station (e.g.: LIM)'}
                      className="uppercase max-w-xs mt-2"
                      required={formData.routeType === 'via'}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Días de operación' : 'Operating days'}</Label>
                  <div className="flex flex-wrap gap-3 pt-1">
                    {DAYS.map(d => (
                      <label key={d.key} className="flex items-center gap-2 cursor-pointer select-none">
                        <Checkbox checked={formData.days[d.key]} onCheckedChange={() => handleDay(d.key)} />
                        <span className="text-sm font-medium text-slate-700">{language === 'es' ? d.es : d.en}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-barlow text-lg font-semibold uppercase tracking-tight text-[#1E3A5F]">
                  {language === 'es' ? 'Imagen' : 'Image'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-2">
                  <Label className="text-[#1E3A5F] font-medium">{language === 'es' ? 'Imagen de la ruta' : 'Route image'} <span className="text-red-500">*</span></Label>
                  {formData.image ? (
                    <div className="relative group">
                      <div className="rounded-lg overflow-hidden border border-slate-200 aspect-video max-w-sm">
                        <img src={getImageSrc(formData.image)} alt="route"
                          className="w-full h-full object-cover"
                          onError={e => e.target.style.display = 'none'} />
                      </div>
                      <Button type="button" variant="destructive" size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleChange('image', '')}>
                        <X className="h-4 w-4" />
                      </Button>
                      <Button type="button" variant="secondary" size="sm"
                        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploading}>
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                          <><Upload className="h-4 w-4 mr-1" />{language === 'es' ? 'Cambiar' : 'Change'}</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div onClick={() => imageInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-12 aspect-video max-w-sm flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-[#1E3A5F] transition-all ${!formData.image ? 'border-red-400 bg-red-50/30' : 'border-slate-300'}`}>
                      {uploading ? <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" /> : (
                        <>
                          <ImageIcon className="h-8 w-8 text-slate-400 mb-2" />
                          <p className="text-sm text-slate-500 text-center">{language === 'es' ? 'Arrastra una imagen o haz clic para seleccionar' : 'Drag an image or click to select'}</p>
                          <p className="text-xs text-slate-400 mt-1">{language === 'es' ? 'Tamaño máximo: 2MB' : 'Max file size: 2MB'}</p>
                        </>
                      )}
                    </div>
                  )}
                  <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden" onChange={e => handleImageUpload(e.target.files?.[0])} />
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </main>
    </div>
  );
};

export default RouteForm;
